import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, } from '@/components/ui/select';
import { useSystemPageDesigns } from '@/features/page-designs';
import { usePageDesignPatterns } from '@/features/page-designs/hooks/use-page-design-patterns';
import { errorHandler } from '@/lib/utils';
import { useStartDesignExperiment } from './use-design-experiments';
const NULL_SLUG = '__inherit__';
export function StartExperimentDialog(p) {
    const { campaignPublicId, seedVariant, children } = p;
    const { t } = useTranslation('campaign');
    const [open, setOpen] = React.useState(false);
    const { data: designs = [] } = useSystemPageDesigns();
    const { data: patterns = [] } = usePageDesignPatterns();
    const [variants, setVariants] = React.useState(() => [
        {
            key: 'A',
            pageDesign: seedVariant.pageDesign ?? NULL_SLUG,
            backgroundPattern: seedVariant.backgroundPattern ?? NULL_SLUG,
            weight: '50',
        },
        {
            key: 'B',
            pageDesign: NULL_SLUG,
            backgroundPattern: NULL_SLUG,
            weight: '50',
        },
    ]);
    const { mutate: start, isPending } = useStartDesignExperiment(campaignPublicId);
    const updateVariant = React.useCallback((idx, patch) => {
        setVariants((prev) => prev.map((v, i) => (i === idx ? { ...v, ...patch } : v)));
    }, []);
    const addVariant = React.useCallback(() => {
        setVariants((prev) => [
            ...prev,
            {
                key: String.fromCharCode(65 + prev.length),
                pageDesign: NULL_SLUG,
                backgroundPattern: NULL_SLUG,
                weight: '50',
            },
        ]);
    }, []);
    const removeVariant = React.useCallback((idx) => {
        setVariants((prev) => (prev.length <= 2 ? prev : prev.filter((_, i) => i !== idx)));
    }, []);
    const handleStart = React.useCallback(() => {
        const payload = variants.map((v) => ({
            key: v.key.trim() || 'X',
            pageDesign: v.pageDesign === NULL_SLUG ? null : v.pageDesign,
            backgroundPattern: v.backgroundPattern === NULL_SLUG ? null : v.backgroundPattern,
            weight: Math.max(0, Number.parseInt(v.weight, 10) || 0),
        }));
        start({ campaignPublicId, variants: payload }, {
            onSuccess: () => {
                toast.success(t('design_experiment.started', { defaultValue: 'Experiment started' }));
                setOpen(false);
            },
            onError: errorHandler,
        });
    }, [variants, start, campaignPublicId, t]);
    return (<Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>{children}</DialogTrigger>
            <DialogContent className="max-w-3xl">
                <DialogHeader>
                    <DialogTitle>
                        {t('design_experiment.start_title', { defaultValue: 'Start a theme A/B test' })}
                    </DialogTitle>
                    <DialogDescription>
                        {t('design_experiment.start_description', {
            defaultValue: 'Pick 2+ variants. Visitors are routed sticky-by-fingerprint, so a returning visitor sees the same theme. Bots are excluded from the experiment.',
        })}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-2">
                    {/* Single header row — labels were duplicating per row before
            and the Weight column was getting squeezed. Stack header
            on mobile, grid layout on sm+. */}
                    <div className="hidden sm:grid grid-cols-[80px_1fr_1fr_80px_36px] gap-2 px-3 text-xs font-medium text-muted-foreground">
                        <span>{t('design_experiment.variant_key', { defaultValue: 'Key' })}</span>
                        <span>{t('design_experiment.theme', { defaultValue: 'Theme' })}</span>
                        <span>{t('design_experiment.pattern', { defaultValue: 'Pattern' })}</span>
                        <span>{t('design_experiment.weight', { defaultValue: 'Weight' })}</span>
                        <span className="sr-only">{t('remove', { defaultValue: 'Remove' })}</span>
                    </div>

                    {variants.map((variant, idx) => (<div key={idx} className="grid grid-cols-1 sm:grid-cols-[80px_1fr_1fr_80px_36px] gap-2 items-center p-3 rounded-md border border-border">
                            <Input value={variant.key} maxLength={16} aria-label={t('design_experiment.variant_key', { defaultValue: 'Key' })} onChange={(e) => updateVariant(idx, { key: e.target.value.toUpperCase() })} className="font-mono"/>

                            <Select value={variant.pageDesign} onValueChange={(v) => updateVariant(idx, { pageDesign: v })}>
                                <SelectTrigger aria-label={t('design_experiment.theme', { defaultValue: 'Theme' })}>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={NULL_SLUG}>
                                        {t('design_experiment.inherit', { defaultValue: 'Inherit campaign' })}
                                    </SelectItem>
                                    {designs.map((d) => (<SelectItem key={d.id} value={d.id}>
                                            {d.name}
                                        </SelectItem>))}
                                </SelectContent>
                            </Select>

                            <Select value={variant.backgroundPattern} onValueChange={(v) => updateVariant(idx, { backgroundPattern: v })}>
                                <SelectTrigger aria-label={t('design_experiment.pattern', { defaultValue: 'Pattern' })}>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={NULL_SLUG}>
                                        {t('design_experiment.inherit', { defaultValue: 'Inherit campaign' })}
                                    </SelectItem>
                                    {patterns.map((pattern) => (<SelectItem key={pattern.slug} value={pattern.slug}>
                                            {pattern.label}
                                        </SelectItem>))}
                                </SelectContent>
                            </Select>

                            <Input type="number" min={0} max={100} value={variant.weight} aria-label={t('design_experiment.weight', { defaultValue: 'Weight' })} onChange={(e) => updateVariant(idx, { weight: e.target.value })} className="tabular-nums"/>

                            <Button variant="ghost" size="icon" disabled={variants.length <= 2} onClick={() => removeVariant(idx)} title={t('remove', { defaultValue: 'Remove' })} aria-label={t('remove', { defaultValue: 'Remove' })} className="justify-self-end">
                                <Trash2 className="h-4 w-4"/>
                            </Button>
                        </div>))}

                    <Button variant="outline" size="sm" onClick={addVariant} disabled={variants.length >= 6}>
                        <Plus className="h-4 w-4 mr-1"/>
                        {t('design_experiment.add_variant', { defaultValue: 'Add variant' })}
                    </Button>
                </div>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" disabled={isPending}>
                            {t('design_experiment.cancel', { defaultValue: 'Cancel' })}
                        </Button>
                    </DialogClose>
                    <Button onClick={handleStart} disabled={isPending}>
                        {t('design_experiment.start_cta', { defaultValue: 'Start experiment' })}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>);
}
